import {
  useRef,
  useState,
  type ComponentProps,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode
} from 'react';
import {ChevronRight} from 'lucide-react';
import {QueueNavTree} from './QueueNavTree';
import {type PolicyViolationQueueSummary} from '@/api/moderationQueue';
import {type QueueFilters} from '@/lib/queueFilters';
import {cn} from '@/lib/utils';

export const SIDEBAR_WIDTH_KEY = 'moderation.queue.sidebar.width';
export const SIDEBAR_MIN_WIDTH = 220;
export const SIDEBAR_MAX_WIDTH = 480;
export const SIDEBAR_DEFAULT_WIDTH = 280;
const KEY_STEP = 16;

const clamp = (width: number) => Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, width));

function storedWidth(): number {
  const raw = Number(localStorage.getItem(SIDEBAR_WIDTH_KEY));
  return Number.isFinite(raw) && raw > 0 ? clamp(raw) : SIDEBAR_DEFAULT_WIDTH;
}

type Props = {
  readonly filters: QueueFilters;
  readonly summaries: readonly PolicyViolationQueueSummary[];
  readonly onSelect: (
    selection: Pick<QueueFilters, 'vertical' | 'classification' | 'scope'>
  ) => void;
  readonly editing?: ComponentProps<typeof QueueNavTree>['editing'];
  // Shown above the verticals tree, inside the same collapsible navigation.
  readonly groups?: ReactNode;
};

// The queue's navigation: a left column on large screens whose right edge can be dragged (or moved with the
// arrow keys) between the minimum and maximum width and whose width is kept in localStorage, and a panel
// above the queue that a button opens on smaller screens, where there is no handle.
export function QueueSidebar({filters, summaries, onSelect, editing, groups}: Props) {
  const [width, setWidth] = useState(storedWidth);
  const [open, setOpen] = useState(false);
  const drag = useRef<{startX: number; startWidth: number} | null>(null);

  const applyWidth = (next: number) => {
    const clamped = clamp(next);
    setWidth(clamped);
    localStorage.setItem(SIDEBAR_WIDTH_KEY, String(clamped));
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    drag.current = {startX: event.clientX, startWidth: width};
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (drag.current) {
      applyWidth(drag.current.startWidth + event.clientX - drag.current.startX);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const target = {
      ArrowLeft: width - KEY_STEP,
      ArrowRight: width + KEY_STEP,
      Home: SIDEBAR_MIN_WIDTH,
      End: SIDEBAR_MAX_WIDTH
    }[event.key];
    if (target !== undefined) {
      event.preventDefault();
      applyWidth(target);
    }
  };

  return (
    <div
      className="relative flex shrink-0 flex-col lg:w-[var(--sidebar-width)] lg:flex-row"
      style={{'--sidebar-width': `${width}px`} as React.CSSProperties}>
      <aside
        aria-label="Queue navigation"
        className="border-border flex min-h-0 min-w-0 flex-1 flex-col gap-2 overflow-auto border-b p-3 lg:border-r lg:border-b-0">
        <button
          type="button"
          aria-expanded={open}
          className="flex items-center gap-1 text-xs font-semibold lg:hidden"
          onClick={() => {
            setOpen((previous) => !previous);
          }}>
          <ChevronRight className={cn('size-3.5', open && 'rotate-90')} />
          Groups and verticals
        </button>
        <div className={cn('min-h-0 flex-col gap-3 lg:flex', open ? 'flex' : 'hidden')}>
          {groups}
          <div className="flex min-h-0 flex-col gap-2">
            <h2 className="text-xs font-semibold">Verticals</h2>
            <QueueNavTree
              filters={filters}
              summaries={summaries}
              editing={editing}
              onSelect={onSelect}
            />
          </div>
        </div>
      </aside>
      {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex -- a focusable separator is the WAI-ARIA window splitter */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize sidebar"
        aria-valuenow={width}
        aria-valuemin={SIDEBAR_MIN_WIDTH}
        aria-valuemax={SIDEBAR_MAX_WIDTH}
        tabIndex={0}
        className="hover:bg-border focus-visible:bg-border absolute inset-y-0 right-0 hidden w-1.5 cursor-col-resize touch-none lg:block"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={() => {
          drag.current = null;
        }}
        onPointerCancel={() => {
          drag.current = null;
        }}
        onKeyDown={onKeyDown}
      />
      {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
    </div>
  );
}
